import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, RefreshCw, Plus, Pencil, Trash2, AlertTriangle, Building2, Lock, Download, Calendar, Upload, Receipt, Clock } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const STATUS_STYLE = {
  calcolata: "bg-slate-100 text-slate-600",
  parziale: "bg-amber-100 text-amber-800",
  incassata: "bg-emerald-100 text-emerald-700",
  esente: "bg-sky-100 text-sky-700",
  anomalia: "bg-rose-100 text-rose-700",
};

const emptyComune = () => ({
  name: "", province: "", region: "", compliance_type: "city_tax",
  tariff_per_person: 0, max_taxable_nights: 14, payment_method: "pagopa",
  payment_periodicity: "trimestrale", exemption_categories: [], reductions: [],
  operational_notes: "", active: true,
});

export default function CityTaxManager() {
  const [meta, setMeta] = useState(null);
  const [positions, setPositions] = useState([]);
  const [totals, setTotals] = useState({});
  const [comuni, setComuni] = useState([]);
  const [dash, setDash] = useState(null);
  const [filters, setFilters] = useState({ comune_id: "", status: "", quarter: "", q: "" });
  const [busy, setBusy] = useState(false);
  const [editComune, setEditComune] = useState(null);

  const canWrite = meta?.can_write;

  const loadComuni = useCallback(() => { api.get("/comuni").then((r) => setComuni(r.data)); }, []);
  const loadPositions = useCallback(() => {
    const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/city-tax/positions${qs ? "?" + qs : ""}`).then((r) => { setPositions(r.data.positions); setTotals(r.data.totals); });
  }, [filters]);

  useEffect(() => { api.get("/city-tax/meta").then((r) => setMeta(r.data)); loadComuni(); }, [loadComuni]);
  useEffect(() => { loadPositions(); }, [loadPositions]);
  const loadDash = useCallback(() => { api.get("/city-tax/dashboard").then((r) => setDash(r.data)); }, []);
  useEffect(() => { loadDash(); }, [loadDash]);

  const backfill = async () => {
    setBusy(true);
    try { const r = await api.post("/city-tax/backfill"); toast.success(`Ricalcolo completato: ${r.data.positions} posizioni`); loadPositions(); loadDash(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const saveComune = async () => {
    try {
      if (editComune.id) await api.put(`/comuni/${editComune.id}`, editComune);
      else await api.post("/comuni", editComune);
      toast.success("Comune salvato"); setEditComune(null); loadComuni(); loadPositions();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const removeComune = async (c) => {
    if (!window.confirm(`Eliminare ${c.name}?`)) return;
    try { await api.delete(`/comuni/${c.id}`); toast.success("Comune eliminato"); loadComuni(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const setEC = (k, v) => setEditComune((c) => ({ ...c, [k]: v }));
  const setExe = (i, k, v) => setEditComune((c) => { const a = [...c.exemption_categories]; a[i] = { ...a[i], [k]: v }; return { ...c, exemption_categories: a }; });

  const quarters = [...new Set(positions.map((p) => p.quarter).filter(Boolean))].sort().reverse();

  return (
    <div className="space-y-6 fade-in" data-testid="city-tax-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Landmark size={20} /></div>
        <div>
          <h2 className="font-head text-xl font-semibold text-slate-900">City Tax Manager</h2>
          <p className="text-sm text-slate-400">Motore fiscale multi-comune · Tassa di soggiorno automatica</p>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList data-testid="city-tax-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="positions" data-testid="tab-positions">Posizioni</TabsTrigger>
          <TabsTrigger value="closing" data-testid="tab-closing">Chiusura & Storico</TabsTrigger>
          <TabsTrigger value="comuni" data-testid="tab-comuni">Comuni</TabsTrigger>
        </TabsList>

        {/* --- DASHBOARD --- */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3" data-testid="city-tax-dashboard">
            {[
              { k: "due", label: "Tassa dovuta", fmt: eur, color: "text-slate-900" },
              { k: "collected", label: "Tassa incassata", fmt: eur, color: "text-emerald-700" },
              { k: "to_collect", label: "Ancora da riscuotere", fmt: eur, color: "text-rose-600" },
              { k: "to_pay_comune", label: "Da versare al Comune", fmt: eur, color: "text-indigo-700" },
              { k: "paid_to_comune", label: "Già versato al Comune", fmt: eur, color: "text-slate-700" },
              { k: "collected_today", label: "Incassato oggi", fmt: eur, color: "text-emerald-600" },
              { k: "collected_quarter", label: `Incassato ${dash?.quarter || "trimestre"}`, fmt: eur, color: "text-emerald-600" },
              { k: "anomalies", label: "Anomalie", fmt: (v) => v, color: "text-amber-600" },
              { k: "without_position", label: "Prenotazioni senza posizione", fmt: (v) => v, color: "text-amber-600" },
              { k: "partial", label: "Con importo parziale", fmt: (v) => v, color: "text-amber-700" },
              { k: "exemptions_to_verify", label: "Esenzioni da verificare", fmt: (v) => v, color: "text-sky-700" },
            ].map((c) => (
              <Card key={c.k} className="p-4" data-testid={`kpi-${c.k}`}>
                <p className="text-xs uppercase text-slate-400">{c.label}</p>
                <p className={`mt-1 text-xl font-bold ${c.color}`}>{dash ? c.fmt(dash.kpi[c.k]) : "…"}</p>
              </Card>
            ))}
          </div>
          <Card className="p-4">
            <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Debito verso Comune (passività — escluso dai ricavi)</p>
            <div className="space-y-1">
              {dash && Object.entries(dash.liability_debito_comune || {}).map(([name, amt]) => (
                <div key={name} className="flex justify-between text-sm" data-testid={`debt-${name}`}>
                  <span className="text-slate-600">{name}</span><b>{eur(amt)}</b>
                </div>
              ))}
              {dash && Object.keys(dash.liability_debito_comune || {}).length === 0 && <p className="text-sm text-slate-400">Nessun debito.</p>}
            </div>
            <p className="mt-3 text-xs italic text-slate-400">{dash?.note}</p>
          </Card>
        </TabsContent>

        {/* --- POSIZIONI --- */}
        <TabsContent value="positions" className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="city-tax-totals">
            {[
              { label: "Tassa dovuta", val: eur(totals.due), color: "text-slate-900" },
              { label: "Incassata", val: eur(totals.collected), color: "text-emerald-700" },
              { label: "Da riscuotere", val: eur(totals.balance), color: "text-rose-600" },
              { label: "Anomalie", val: totals.anomalies ?? 0, color: "text-amber-600" },
            ].map((c) => (
              <Card key={c.label} className="p-4">
                <p className="text-xs uppercase text-slate-400">{c.label}</p>
                <p className={`mt-1 text-lg font-bold ${c.color}`}>{c.val}</p>
              </Card>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.comune_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, comune_id: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-9 w-44" data-testid="filter-comune"><SelectValue placeholder="Comune" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti i Comuni</SelectItem>{comuni.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-9 w-40" data-testid="filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{(meta?.statuses || []).map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.quarter || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, quarter: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-9 w-36" data-testid="filter-quarter"><SelectValue placeholder="Trimestre" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{quarters.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="filter-search" placeholder="Cerca ospite/appartamento…" className="h-9 w-56" value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
            {canWrite && (
              <Button data-testid="backfill-btn" variant="outline" className="ml-auto h-9 gap-2" onClick={backfill} disabled={busy}>
                <RefreshCw size={15} className={busy ? "animate-spin" : ""} /> Ricalcola posizioni
              </Button>
            )}
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="positions-table">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400">
                  <tr>
                    <th className="p-3">Comune</th><th className="p-3">Appartamento</th><th className="p-3">Ospite</th>
                    <th className="p-3">Check-in → out</th><th className="p-3 text-center">Osp.</th><th className="p-3 text-center">Notti</th>
                    <th className="p-3 text-center">Imp.</th><th className="p-3 text-right">Dovuto</th><th className="p-3">Trim.</th><th className="p-3">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`position-row-${p.id}`}>
                      <td className="p-3">{p.comune_name || "—"}</td>
                      <td className="p-3">{p.apartment_name}</td>
                      <td className="p-3">{p.guest_name || "—"}</td>
                      <td className="p-3 text-xs text-slate-500">{p.check_in} → {p.check_out}</td>
                      <td className="p-3 text-center">{p.guests}</td>
                      <td className="p-3 text-center">{p.nights}</td>
                      <td className="p-3 text-center">{p.taxable_nights}</td>
                      <td className="p-3 text-right font-semibold">{eur(p.amount_due)}</td>
                      <td className="p-3 text-xs text-slate-500">{p.quarter}</td>
                      <td className="p-3">
                        <Badge className={STATUS_STYLE[p.status] || "bg-slate-100"}>
                          {p.status === "anomalia" && <AlertTriangle size={11} className="mr-1" />}{p.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {positions.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-400">Nessuna posizione. Usa "Ricalcola posizioni" per generarle dalle prenotazioni.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* --- CHIUSURA & STORICO --- */}
        <TabsContent value="closing">
          <ClosingTab comuni={comuni} canWrite={canWrite} onChange={() => { loadDash(); loadPositions(); }} />
        </TabsContent>

        {/* --- COMUNI --- */}
        <TabsContent value="comuni" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Ogni Comune è configurabile: tariffa, notti imponibili, esenzioni, periodicità. Aggiungere un Comune non richiede modifiche al codice.</p>
            {canWrite && <Button data-testid="add-comune-btn" className="gap-2" onClick={() => setEditComune(emptyComune())}><Plus size={16} /> Nuovo Comune</Button>}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {comuni.map((c) => (
              <Card key={c.id} className="p-4" data-testid={`comune-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-primary" />
                    <div>
                      <p className="font-semibold text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-400">{[c.province, c.region].filter(Boolean).join(" · ")}</p>
                    </div>
                  </div>
                  {!c.active && <Badge className="bg-slate-100 text-slate-500">Disattivo</Badge>}
                </div>
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <div className="flex justify-between"><span className="text-slate-400">Tariffa/persona</span><b>{eur(c.tariff_per_person)}</b></div>
                  <div className="flex justify-between"><span className="text-slate-400">Notti max imponibili</span><b>{c.max_taxable_nights}</b></div>
                  <div className="flex justify-between"><span className="text-slate-400">Periodicità</span><b className="capitalize">{c.payment_periodicity}</b></div>
                  <div className="flex justify-between"><span className="text-slate-400">Esenzioni</span><b>{(c.exemption_categories || []).length}</b></div>
                </div>
                {canWrite && (
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1 gap-1.5" data-testid={`edit-comune-${c.id}`} onClick={() => setEditComune({ ...c })}><Pencil size={13} /> Modifica</Button>
                    <Button size="sm" variant="ghost" className="text-rose-500" data-testid={`delete-comune-${c.id}`} onClick={() => removeComune(c)}><Trash2 size={14} /></Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Comune editor */}
      <Dialog open={!!editComune} onOpenChange={(o) => !o && setEditComune(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="comune-dialog">
          <DialogHeader><DialogTitle>{editComune?.id ? "Modifica Comune" : "Nuovo Comune"}</DialogTitle>
            <DialogDescription>Configura tariffa, notti imponibili, esenzioni e periodicità di versamento del Comune.</DialogDescription>
          </DialogHeader>
          {editComune && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div><label className="text-xs text-slate-500">Nome *</label><Input data-testid="comune-name" value={editComune.name} onChange={(e) => setEC("name", e.target.value)} /></div>
                <div><label className="text-xs text-slate-500">Provincia</label><Input value={editComune.province} onChange={(e) => setEC("province", e.target.value)} /></div>
                <div><label className="text-xs text-slate-500">Regione</label><Input value={editComune.region} onChange={(e) => setEC("region", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div><label className="text-xs text-slate-500">Tariffa/persona/notte (€)</label><Input data-testid="comune-tariff" type="number" step="0.01" value={editComune.tariff_per_person} onChange={(e) => setEC("tariff_per_person", +e.target.value)} /></div>
                <div><label className="text-xs text-slate-500">Notti max imponibili</label><Input data-testid="comune-maxnights" type="number" value={editComune.max_taxable_nights} onChange={(e) => setEC("max_taxable_nights", +e.target.value)} /></div>
                <div>
                  <label className="text-xs text-slate-500">Periodicità</label>
                  <Select value={editComune.payment_periodicity} onValueChange={(v) => setEC("payment_periodicity", v)}>
                    <SelectTrigger data-testid="comune-periodicity"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.periodicities || ["trimestrale"]).map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Modalità versamento</label>
                <Select value={editComune.payment_method} onValueChange={(v) => setEC("payment_method", v)}>
                  <SelectTrigger data-testid="comune-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.payment_methods || ["pagopa"]).map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase text-slate-500">Categorie esenzione</label>
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="add-exemption"
                    onClick={() => setEC("exemption_categories", [...editComune.exemption_categories, { code: "", label: "", requires_doc: false, active: true }])}>+ Aggiungi</Button>
                </div>
                <div className="space-y-2">
                  {editComune.exemption_categories.map((ex, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2">
                      <Input className="h-8 flex-1 text-sm" placeholder="Etichetta esenzione" value={ex.label} onChange={(e) => setExe(i, "label", e.target.value)} />
                      <label className="flex items-center gap-1 text-xs text-slate-500"><Switch checked={ex.active} onCheckedChange={(v) => setExe(i, "active", v)} /> attiva</label>
                      <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => setEC("exemption_categories", editComune.exemption_categories.filter((_, j) => j !== i))}><Trash2 size={13} /></Button>
                    </div>
                  ))}
                </div>
              </div>
              <div><label className="text-xs text-slate-500">Note operative</label><Textarea rows={2} value={editComune.operational_notes} onChange={(e) => setEC("operational_notes", e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={editComune.active} onCheckedChange={(v) => setEC("active", v)} data-testid="comune-active" /> Comune attivo</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditComune(null)}>Annulla</Button>
            <Button data-testid="save-comune-btn" onClick={saveComune} disabled={!editComune?.name}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Chiusura trimestre, versamenti, promemoria ---------------- */
const REMINDER_STYLE = { pending: "bg-amber-100 text-amber-800", partial: "bg-sky-100 text-sky-700", paid: "bg-emerald-100 text-emerald-700" };

function ClosingTab({ comuni, canWrite, onChange }) {
  const [periods, setPeriods] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [closeForm, setCloseForm] = useState({ comune_id: "", quarter: "" });
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", paid_date: "", pagopa_ref: "", note: "", receipt_url: "" });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/city-tax/periods").then((r) => setPeriods(r.data));
    api.get("/city-tax/reminders").then((r) => setReminders(r.data));
    api.get("/city-tax/payments").then((r) => setPayments(r.data));
    api.get("/city-tax/positions").then((r) => setQuarters([...new Set(r.data.positions.map((p) => p.quarter).filter(Boolean))].sort().reverse()));
  }, []);
  useEffect(() => { load(); }, [load]);

  const closeQuarter = async () => {
    if (!closeForm.comune_id || !closeForm.quarter) { toast.error("Seleziona Comune e trimestre"); return; }
    if (!window.confirm(`Chiudere il trimestre ${closeForm.quarter}? Il periodo verrà bloccato e non sarà più modificabile.`)) return;
    try {
      const r = await api.post("/city-tax/periods/close", closeForm);
      toast.success(`Trimestre chiuso · da versare ${eur(r.data.period.total_to_pay)}`);
      setCloseForm({ comune_id: "", quarter: "" }); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const download = async (pid, fmt) => {
    try {
      const r = await api.get(`/city-tax/periods/${pid}/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `tassa_soggiorno.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPayForm((f) => ({ ...f, receipt_url: r.data.url }));
      toast.success("Ricevuta caricata");
    } catch { toast.error("Errore upload"); }
    finally { setUploading(false); }
  };

  const savePayment = async () => {
    if (!payForm.amount) { toast.error("Inserisci l'importo"); return; }
    try {
      await api.post("/city-tax/payments", {
        comune_id: payFor.comune_id, period_id: payFor.id, quarter: payFor.quarter,
        amount: Number(payForm.amount), paid_date: payForm.paid_date, pagopa_ref: payForm.pagopa_ref,
        note: payForm.note, receipt_url: payForm.receipt_url,
      });
      toast.success("Versamento registrato"); setPayFor(null);
      setPayForm({ amount: "", paid_date: "", pagopa_ref: "", note: "", receipt_url: "" });
      load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-5" data-testid="closing-tab">
      {/* Chiudi trimestre */}
      {canWrite && (
        <Card className="p-4" data-testid="close-quarter-card">
          <div className="mb-3 flex items-center gap-2"><Lock size={16} className="text-primary" /><h3 className="font-semibold text-slate-900">Chiudi trimestre</h3></div>
          <div className="flex flex-wrap items-end gap-2">
            <div><label className="text-xs text-slate-500">Comune</label>
              <Select value={closeForm.comune_id || ""} onValueChange={(v) => setCloseForm((f) => ({ ...f, comune_id: v }))}>
                <SelectTrigger className="h-9 w-48" data-testid="close-comune"><SelectValue placeholder="Comune" /></SelectTrigger>
                <SelectContent>{comuni.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Trimestre</label>
              <Select value={closeForm.quarter || ""} onValueChange={(v) => setCloseForm((f) => ({ ...f, quarter: v }))}>
                <SelectTrigger className="h-9 w-36" data-testid="close-quarter"><SelectValue placeholder="Trimestre" /></SelectTrigger>
                <SelectContent>{quarters.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="h-9 gap-2 bg-primary" data-testid="close-quarter-btn" onClick={closeQuarter}><Lock size={15} /> Chiudi trimestre</Button>
          </div>
          <p className="mt-2 text-xs text-slate-400">Blocca il periodo, calcola il totale da versare, genera i report (PDF/Excel/CSV) e crea il promemoria del versamento.</p>
        </Card>
      )}

      {/* Promemoria versamenti */}
      <Card className="p-4" data-testid="reminders-card">
        <div className="mb-3 flex items-center gap-2"><Clock size={16} className="text-amber-600" /><h3 className="font-semibold text-slate-900">Promemoria versamenti al Comune</h3></div>
        {reminders.length === 0 ? <p className="text-sm text-slate-400">Nessun promemoria. Chiudi un trimestre per generarlo.</p> : (
          <div className="space-y-2">
            {reminders.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-2.5" data-testid={`reminder-${r.id}`}>
                <div className="flex items-center gap-3">
                  <Calendar size={15} className={r.overdue ? "text-rose-500" : "text-slate-400"} />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{r.comune_name} · {r.quarter}</p>
                    <p className="text-xs text-slate-400">Scadenza {r.due_date}{r.overdue ? " · SCADUTO" : (r.days_left != null && r.status !== "paid" ? ` · tra ${r.days_left} gg` : "")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{eur(r.amount)}</span>
                  <Badge className={r.overdue && r.status !== "paid" ? "bg-rose-100 text-rose-700" : (REMINDER_STYLE[r.status] || "bg-slate-100")}>
                    {r.overdue && r.status !== "paid" ? "scaduto" : r.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Storico trimestri chiusi */}
      <Card className="p-4" data-testid="periods-card">
        <h3 className="mb-3 font-semibold text-slate-900">Storico trimestri chiusi</h3>
        {periods.length === 0 ? <p className="text-sm text-slate-400">Nessun trimestre chiuso.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400">
                <tr><th className="p-2">Comune</th><th className="p-2">Trimestre</th><th className="p-2 text-right">Dovuto</th><th className="p-2 text-right">Incassato</th><th className="p-2">Chiuso da</th><th className="p-2 text-right">Report / Versamento</th></tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`period-${p.id}`}>
                    <td className="p-2">{p.comune_name}</td>
                    <td className="p-2"><Badge className="bg-slate-100 text-slate-600">{p.quarter}</Badge></td>
                    <td className="p-2 text-right">{eur(p.total_due)}</td>
                    <td className="p-2 text-right font-semibold text-emerald-700">{eur(p.total_collected)}</td>
                    <td className="p-2 text-xs text-slate-500">{p.closed_by}</td>
                    <td className="p-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`export-pdf-${p.id}`} onClick={() => download(p.id, "pdf")}><Download size={12} /> PDF</Button>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`export-xlsx-${p.id}`} onClick={() => download(p.id, "xlsx")}><Download size={12} /> Excel</Button>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`export-csv-${p.id}`} onClick={() => download(p.id, "csv")}><Download size={12} /> CSV</Button>
                        {canWrite && <Button size="sm" className="h-8 gap-1 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid={`pay-${p.id}`} onClick={() => { setPayFor(p); setPayForm({ amount: String(p.total_to_pay || ""), paid_date: "", pagopa_ref: "", note: "", receipt_url: "" }); }}><Receipt size={12} /> Versamento</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Storico versamenti */}
      <Card className="p-4" data-testid="payments-card">
        <h3 className="mb-3 font-semibold text-slate-900">Storico versamenti</h3>
        {payments.length === 0 ? <p className="text-sm text-slate-400">Nessun versamento registrato.</p> : (
          <div className="space-y-2">
            {payments.map((pay) => (
              <div key={pay.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-2.5 text-sm" data-testid={`payment-${pay.id}`}>
                <div>
                  <p className="font-medium text-slate-800">{pay.comune_name} · {pay.quarter}</p>
                  <p className="text-xs text-slate-400">{pay.paid_date} · {pay.pagopa_ref || "—"} · {pay.operator}</p>
                </div>
                <div className="flex items-center gap-2">
                  {pay.receipt_url && <a href={pay.receipt_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline">Ricevuta</a>}
                  <span className="font-semibold">{eur(pay.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dialog versamento */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Registra versamento · {payFor?.comune_name} {payFor?.quarter}</DialogTitle>
            <DialogDescription>Versamento al Comune con riferimento PagoPA e ricevuta allegata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Importo (€)</label><Input data-testid="pay-amount" type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-500">Data pagamento</label><Input data-testid="pay-date" type="date" value={payForm.paid_date} onChange={(e) => setPayForm((f) => ({ ...f, paid_date: e.target.value }))} /></div>
            </div>
            <div><label className="text-xs text-slate-500">Riferimento PagoPA</label><Input data-testid="pay-pagopa" value={payForm.pagopa_ref} onChange={(e) => setPayForm((f) => ({ ...f, pagopa_ref: e.target.value }))} placeholder="RF…" /></div>
            <div>
              <label className="text-xs text-slate-500">Ricevuta (PDF/immagine)</label>
              <div className="flex items-center gap-2">
                <input id="receipt-upload" type="file" accept="image/*,.pdf" className="hidden" onChange={uploadReceipt} data-testid="pay-receipt-input" />
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => document.getElementById("receipt-upload").click()} disabled={uploading}><Upload size={14} /> {uploading ? "Caricamento…" : "Carica ricevuta"}</Button>
                {payForm.receipt_url && <span className="text-xs text-emerald-600">Ricevuta allegata ✓</span>}
              </div>
            </div>
            <div><label className="text-xs text-slate-500">Nota</label><Textarea rows={2} value={payForm.note} onChange={(e) => setPayForm((f) => ({ ...f, note: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Annulla</Button>
            <Button data-testid="save-payment-btn" onClick={savePayment} className="bg-indigo-600 hover:bg-indigo-700">Registra versamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
